import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { licenseApi } from '@/lib/api';

const PLAN_LABELS: Record<string, string> = {
  demo:    'Free Demo',
  onprem:  'On-Premise',
  cloud:   'Cloud',
  expired: 'Expired',
};

export function LicensePage() {
  const qc = useQueryClient();
  const [key, setKey]       = useState('');
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: ['license-status'],
    queryFn:  licenseApi.status,
  });

  const activate = useMutation({
    mutationFn: (k: string) => licenseApi.activate(k),
    onSuccess: () => {
      setSuccess('License activated successfully.');
      setError('');
      setKey('');
      qc.invalidateQueries({ queryKey: ['license-status'] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setSuccess('');
    },
  });

  if (isLoading) return <div className="p-6 text-slate-500">Loading...</div>;

  return (
    <div className="max-w-xl">
      <h1 className="text-xl font-bold text-slate-900 mb-6">License</h1>

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-sm text-slate-500 mb-1">Current Plan</p>
            <p className="text-lg font-semibold text-slate-900">{PLAN_LABELS[status?.plan ?? ''] ?? status?.plan}</p>
          </div>
          {status?.expiresAt && (
            <div className="text-right">
              <p className="text-sm text-slate-500 mb-1">
                {status.plan === 'demo' ? 'Demo ends' : 'License expires'}
              </p>
              <p className="text-sm font-medium text-slate-900">
                {new Date(status.expiresAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
        {status?.graceDays != null && (
          <p className="mt-3 text-sm text-amber-600 font-medium">
            ⚠️ {status.graceDays} day{status.graceDays === 1 ? '' : 's'} remaining in grace period
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-semibold text-slate-800 mb-1">Activate License Key</h2>
        <p className="text-sm text-slate-500 mb-4">
          Paste your on-premise license key here to activate or renew your license.
        </p>
        <textarea
          value={key}
          onChange={e => setKey(e.target.value)}
          placeholder="Paste license key..."
          rows={4}
          className="w-full border border-slate-200 rounded-lg p-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none mb-3"
        />
        {error   && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {success && <p className="text-sm text-green-600 mb-3">{success}</p>}
        <button
          onClick={() => activate.mutate(key.trim())}
          disabled={!key.trim() || activate.isPending}
          className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-2 px-5 rounded-lg text-sm transition-colors"
        >
          {activate.isPending ? 'Activating...' : 'Activate'}
        </button>
      </div>
    </div>
  );
}
